/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '_metronic/assets/ts/_utils'
import {useThemeMode} from '_metronic/partials'
import _ from 'lodash'
import dayjs from 'dayjs'
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers-pro'
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs'
import {TextField} from '@mui/material'
import {noDataImg} from 'assets/imageDeclear'

type Props = {
  className: string
  deviceCount: any
  date: any
  setDate: any
  setDeviceCount: any
}

const RegsterBarChart: React.FC<Props> = ({
  className,
  deviceCount,
  date,
  setDate,
  setDeviceCount,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, date.$y])
  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(chartRef.current, getChartOptions(height, deviceCount, date))
    if (chart) {
      chart.render()
    }

    return chart
  }

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        {/* begin::Title */}
        <h3 className='card-title align-items-start flex-column w-100'>
          <div className={'d-flex justify-content-between w-100'}>
            <div className={'align-self-end'}>
              <span className='card-label fw-bold fs-3 mb-1'>단말기 등록현황</span>
              <span className='text-muted fw-semibold fs-7'>
                미터리움 단말기 등록 현황을 보여줍니다.
              </span>
            </div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='날짜 선택'
                value={date}
                views={['year']}
                openTo='year'
                mask={'____'}
                disableFuture={true}
                onChange={(newValue: any) => {
                  setDeviceCount(null)
                  setDate(dayjs(newValue))
                }}
                className={'w-200px'}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </div>
        </h3>
        {/* end::Title */}
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* begin::Chart */}
        {deviceCount?.length > 0 ? (
          <div ref={chartRef} id='kt_charts_widget_1_chart' style={{height: '700px'}} />
        ) : (
          <div className={'text-center'}>
            <img alt={'no-data'} src={noDataImg} style={{width: 1000}} className={'rounded-4'} />
          </div>
        )}
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {RegsterBarChart}

function getChartOptions(height: number, deviceCount: any, date: any): ApexOptions {
  const labelColor = getCSSVariableValue('--kt-gray-500')
  const borderColor = getCSSVariableValue('--kt-gray-200')
  const baseColor = getCSSVariableValue('--kt-primary')
  const secondaryColor = getCSSVariableValue('--kt-gray-300')
  return {
    series: _.map(deviceCount, (el) => {
      return {
        name: el.cooperName,
        data: _.map(el.monthList, (el2) => {
          return el2.deviceCount
        }),
      }
    }),
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      stacked: true,
      height: height,
      zoom: {
        enabled: true,
      },
      toolbar: {
        export: {
          png: {
            filename: '단말기 등록현황',
          },
          csv: {
            filename: '단말기 등록현황',
          },
        },
      },
    },

    legend: {
      show: true,
      position: 'bottom',
      fontSize: '22px',
      fontWeight: 900,
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 10,
        dataLabels: {
          position: 'center', // top, center, bottom
        },
      },
    },
    dataLabels: {
      enabled: true,

      style: {
        fontSize: '18px',
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: _.map(_.range(1, 13), (i) =>
        dayjs(date)
          .month(i - 1)
          .format('YY년 MM월')
      ),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: '#252525',
          fontSize: '18px',
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      tickAmount: 10,
      labels: {
        style: {
          colors: '#252525',
          fontSize: '18px',
        },
        formatter(val: number, opts?: any): string | string[] {
          return val + '대'
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '18px',
      },
      y: {
        formatter: function (val) {
          return '총 ' + val + ' 대'
        },
      },
    },
    colors: [baseColor, '#f3a041'],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}
